'use client';

import { createContext, useContext, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Language, translations, getDirection } from './i18n';

const SUPPORTED_LOCALES: Language[] = ['ar', 'en', 'fr'];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.ar;
  dir: 'rtl' | 'ltr';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  // Extract locale from pathname (e.g., /ar/pricing -> ar)
  const language = useMemo((): Language => {
    const segments = pathname.split('/').filter(Boolean);
    const firstSegment = segments[0] as Language;
    if (SUPPORTED_LOCALES.includes(firstSegment)) {
      return firstSegment;
    }
    return 'ar'; // default locale
  }, [pathname]);

  // Handle language change by navigating to the new locale
  const setLanguage = useCallback((newLang: Language) => {
    // Extract path segments
    const segments = pathname.split('/').filter(Boolean);

    // Check if the first segment is a locale and remove it
    if (SUPPORTED_LOCALES.includes(segments[0] as Language)) {
      segments.shift();
    }

    // Build new path with new locale
    const pathWithoutLocale = segments.length > 0 ? `/${segments.join('/')}` : '';
    const newPath = `/${newLang}${pathWithoutLocale}`;

    router.push(newPath);
  }, [pathname, router]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: translations[language] || translations.ar,
    dir: getDirection(language)
  }), [language, setLanguage]);

  // Sync HTML attributes with language (Fixes Visual and Hydration LTR/RTL mismatch)
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = getDirection(language);
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      <div className="min-h-screen">
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
